import React, { useEffect, useRef } from 'react';

/**
 * Golden Matrix Rain — falling characters on a transparent canvas.
 * Tuned for cinematic warmth (gold/amber palette) rather than the classic green.
 */
export default function MatrixRain({ opacity = 0.55, fontSize = 16, density = 1.1 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const dropsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*+=-/<>{}[]|';
    let width = 0;
    let height = 0;
    let columns = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      columns = Math.floor(width / fontSize / density);
      dropsRef.current = new Array(columns).fill(0).map(() => Math.random() * -50);
    };
    resize();
    window.addEventListener('resize', resize);

    let last = 0;
    const step = (t) => {
      if (t - last > 55) {
        last = t;
        // fade previous frame for the trail
        ctx.fillStyle = 'rgba(6, 4, 2, 0.18)';
        ctx.fillRect(0, 0, width, height);

        ctx.font = `${fontSize}px "Share Tech Mono", "Courier New", monospace`;
        ctx.textBaseline = 'top';

        const drops = dropsRef.current;
        for (let i = 0; i < drops.length; i++) {
          const x = i * fontSize * density;
          const y = drops[i] * fontSize;
          const ch = glyphs.charAt(Math.floor(Math.random() * glyphs.length));

          // leading glyph: bright gold/cream
          ctx.fillStyle = `rgba(255, 230, 160, ${opacity})`;
          ctx.shadowColor = 'rgba(255, 190, 90, 0.9)';
          ctx.shadowBlur = 10;
          ctx.fillText(ch, x, y);

          // trail glyph: deeper amber
          ctx.shadowBlur = 0;
          ctx.fillStyle = `rgba(200, 140, 50, ${opacity * 0.55})`;
          ctx.fillText(glyphs.charAt(Math.floor(Math.random() * glyphs.length)), x, y - fontSize);

          if (y > height && Math.random() > 0.975) drops[i] = 0;
          drops[i] += 1;
        }
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [opacity, fontSize, density]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="matrix-rain-canvas"
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: 2, mixBlendMode: 'screen' }}
    />
  );
}
